

















































































import { Component, Vue, Prop, Emit, Watch } from 'vue-property-decorator';
import debounce from 'lodash/debounce';
import AutocompleteList from './AutocompleteList.vue';
import { websiteText } from '@/mixins/websiteTexts';

export interface SearchItem {
    id: string | number;
    description: string;
    [key: string]: any;
}

@Component({ components: { AutocompleteList } })
class GenericSearch extends Vue {
    @Prop({ default: '' }) placeholder!: string;
    @Prop({ default: false }) isInModal!: boolean;
    @Prop({ default: false }) showCurrentLocationButton!: boolean;
    @Prop({ default: 3 }) minQueryLength!: number;
    @Prop({ default: false }) hasAutocomplete!: boolean;
    @Prop({ default: false }) inEveryday!: boolean;
    @Prop({ default: false }) noMarginTop!: boolean;
    @Prop({ default: false }) autoCompleteTopMobile!: boolean;

    @Prop({ default: () => websiteText('meeting.modal.clear.search') }) clearLabel!: string;
    @Prop({ default: () => websiteText('meeting.modal.submit.search') }) submitLabel!: string;
    @Prop({ default: '' }) searchId!: string;

    @Prop({ default: () => [] }) results!: SearchItem[];
    @Prop({ default: false }) isLoading!: boolean;

    @Prop({ default: '' }) initialQuery!: string;
    @Prop({ default: false }) inMegaMenu!: boolean;
    @Prop({ default: false }) hideOnOutOfFocus!: boolean;

    public localQuery: string = '';
    public selectedIndex: number = -1;
    public shouldShowYourLocation = false;
    public isSlowTyper = false;
    public a11yStatusMessage = '';
    public hasFocus = false;
    private slowTypingTimer: number | null = null;

    @Watch('results')
    onResultsChanged(newResults: SearchItem[]) {
        if (newResults.length > 0) {
            this.a11yStatusMessage = websiteText('meeting.a11y.results.available').replace('{0}', newResults.length.toString());
        } else if (this.localQuery.length >= this.minQueryLength && !this.isLoading) {
            this.a11yStatusMessage = websiteText('meeting.a11y.no.results').replace('{0}', this.localQuery);
        }
    }

    @Watch('initialQuery', { immediate: true })
    onInitialQueryChange(newVal: string) {
        if(this.hideOnOutOfFocus) {
            this.localQuery = '';
            return;
        }
        if (newVal) this.localQuery = newVal;
    }

    public focusInput() {
        const input = this.$refs.searchInput as HTMLInputElement;
        if (input) {
            input.focus();
        }
    }

    public inputChange(query: string = '') {
        if (query === '') {
            this.clearInput();
            return;
        }

        if (this.slowTypingTimer) {
            window.clearTimeout(this.slowTypingTimer);
        }

        this.slowTypingTimer = window.setTimeout(() => {
            this.isSlowTyper = this.localQuery.length > 0 && this.localQuery.length < this.minQueryLength;
        }, 1000);

        this.debouncedQuery(query);
    }

    private debouncedQuery = debounce((query: string) => {
        if (query.length >= this.minQueryLength) {
            this.emitQueryChange(query);
        }
    }, 100);

    @Emit('submit')
    public emitSubmit(query: string) {
        return query;
    }

    @Emit('query-changed')
    private emitQueryChange(query: string) {
        this.a11yStatusMessage = websiteText('meeting.a11y.searching');
        return query;
    }

    private setHeaderOpen(open: boolean) {
        const el = this.$el as HTMLElement | undefined;
        const wrapper = el && el.closest('#headerSearchBar, #headerSearchBarMobile');
        if (wrapper) wrapper.classList.toggle('open', open);
    }

    public onFocusOut(event: FocusEvent) {
        const currentTarget = event.currentTarget as HTMLElement;
        const relatedTarget = event.relatedTarget as HTMLElement | null;
        if (!currentTarget.contains(relatedTarget)) {
            this.shouldShowYourLocation = false;
        }
        setTimeout(() => {
            const activeElement = document.activeElement;
            this.hasFocus = !!(activeElement && (this.$el as HTMLElement).contains(activeElement));
            this.setHeaderOpen(this.hasFocus);

            if (!this.hasFocus && this.hideOnOutOfFocus) {
                this.localQuery = '';
                this.$emit('clear');
            }
        }, 0);
        const searchMobile = document.querySelector('#headerSearchBarMobile');
        const searchDesktop = document.querySelector('#headerSearchBar');
        if(searchMobile) searchMobile.classList.remove('open');
        if(searchDesktop) searchDesktop.classList.remove('open');
    }

    onFocusIn() {
        this.hasFocus = true;
        this.setHeaderOpen(true);
    }

    @Emit('clear')
    public clearInput() {
        this.localQuery = '';
        this.selectedIndex = -1;
        this.isSlowTyper = false;
        this.$nextTick(() => {
            const input = this.$refs.searchInput as HTMLInputElement;
            if (input) input.focus();
        });
    }

    public searchFocus() {
        this.shouldShowYourLocation = this.results.length === 0 && this.showCurrentLocationButton;

        if (this.inMegaMenu) {
            setTimeout(() => {
                const input = this.$refs.searchInput as HTMLInputElement;
                if (!input) return;

                const rect = input.getBoundingClientRect();
                //@ts-ignore
                const viewport = window.visualViewport;
                
                const isAboveScreen = rect.top < 0;
                const isBelowKeyboard = viewport && rect.bottom > viewport.height;

                if (isAboveScreen || isBelowKeyboard) {
                    input.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }, 300);
        }
    }

    @Emit('use-location')
    public handleUseCurrentLocation() {
        this.shouldShowYourLocation = false;
        this.selectedIndex = -1;
    }

    public updateIndex(index: number) {
        this.selectedIndex = index;
    }

    public pressSearch(query?: string) {
        if (this.selectedIndex > -1 && this.results[this.selectedIndex]) {
            this.selectItem(this.results[this.selectedIndex]);
        } else if (query && query.length >= this.minQueryLength) {
            this.emitSubmit(query);
            this.selectedIndex = -1;
        }
    }

    get activeDescendantId(): string | undefined {
        if (this.selectedIndex === -1) {
            return undefined;
        }

        const prefix = this.searchId || 'generic';

        if (this.results.length === 0 && this.shouldShowYourLocation && this.selectedIndex === 0) {
            return `${prefix}-quick-search-location`; 
        }

        if (this.results.length > 0 && this.results[this.selectedIndex]) {
            return `${prefix}-item-${this.results[this.selectedIndex].id}`;
        }
        return undefined;
    }

    public onEnter() {
        if (this.results.length === 0 && this.shouldShowYourLocation && this.selectedIndex === 0) {
            this.handleUseCurrentLocation();
            return;
        }

        if (this.selectedIndex > -1 && this.results[this.selectedIndex]) {
            this.selectItem(this.results[this.selectedIndex]);
            return;
        }

        if (this.localQuery.length >= this.minQueryLength) {
            this.emitSubmit(this.localQuery);
            this.selectedIndex = -1;
        }
    }

    public onEsc(e: KeyboardEvent) {
        this.selectedIndex = -1;
        this.$emit('close-dropdown');
    }

    public navigateList(direction: number) {
        const maxIndex = this.results.length > 0 ? this.results.length - 1 : this.shouldShowYourLocation ? 0 : -1;

        if (maxIndex === -1) return;

        this.selectedIndex += direction;

        if (this.selectedIndex < 0) this.selectedIndex = maxIndex;
        if (this.selectedIndex > maxIndex) this.selectedIndex = 0;
    }

    @Emit('item-selected')
    public selectItem(result: SearchItem) {
        this.localQuery = result.description;
        this.selectedIndex = -1;
        return result;
    }
}

export default GenericSearch;
